import { SearchFilterContentTypes } from './enums';

export const AUDIO_SEARCH_ROUTE = '/audio/search';
export const IMAGE_SEARCH_ROUTE = '/images/search';
export const VIDEO_SEARCH_ROUTE =
  '/:path(all-video/.*|motion-graphics/.*|video/.*|templates/.*)';

export interface SearchUrlProps {
  searchRoute?: string;
  contentType?: SearchFilterContentTypes;
  searchTerm?: string;
  mediaType?: string;
  categoryUrlId?: string;
  contributorPortalId?: string;
  searchOrigin?: string;
  similarTo?: number;
  usageRights?: string;
}

export function getSearchUrl(props: SearchUrlProps): string {
  props.searchRoute = getSearchRoute(props);
  const searchPath = getSearchPath(props);
  const searchQuery = getSearchParams(props).toString();
  return searchQuery ? `${searchPath}?${searchQuery}` : searchPath;
}

// searchRoute is one of the *_SEARCH_ROUTE constant values above
function getSearchRoute(props: SearchUrlProps): string {
  if (props.searchRoute) {
    return props.searchRoute;
  } else if (isAssetId(props.searchTerm)) {
    return getSearchRouteByAssetId(props.searchTerm);
  } else {
    return getSearchRouteByContentType(props.contentType);
  }
}

// searchPath is the actual url path we generate
function getSearchPath(props: SearchUrlProps): string {
  let searchPath = isFlatUrl(props.searchRoute)
    ? `/${getMediaTypePath(props.contentType)}/search`
    : props.searchRoute;

  if (props.searchTerm) {
    const cleanedTerm = encodeURIComponent(props.searchTerm).replace(
      /%20|%2520|%2b| /g,
      '-'
    );
    searchPath += `/${cleanedTerm}`;
  }
  return searchPath;
}

function getSearchParams(props: SearchUrlProps): URLSearchParams {
  const searchParams = new URLSearchParams();
  if (props.searchOrigin) {
    searchParams.append('search-origin', props.searchOrigin);
  }
  if (props.mediaType) {
    searchParams.append('media-type', encodeURIComponent(props.mediaType));
  } else if (!isFlatUrl(props.searchRoute) && props.contentType) {
    const mediaType = getMediaTypeParam(props.contentType);
    if (mediaType) {
      searchParams.append('media-type', encodeURIComponent(mediaType));
    }
  }
  if (props.similarTo) {
    searchParams.append('search_similar_id', props.similarTo.toString());
  }
  if (props.usageRights && props.usageRights !== 'all') {
    searchParams.append('usageRights', props.usageRights);
  }
  if (props.categoryUrlId) {
    searchParams.append('categories', encodeURIComponent(props.categoryUrlId));
  }
  if (props.contributorPortalId) {
    searchParams.append(
      'portal_artist_ids',
      encodeURIComponent(props.contributorPortalId)
    );
  }
  return searchParams;
}

function isFlatUrl(searchRoute: string): boolean {
  return searchRoute === VIDEO_SEARCH_ROUTE;
}

function getMediaTypeParam(
  contentType: SearchFilterContentTypes | string
): string {
  switch (contentType) {
    case SearchFilterContentTypes.All_audio_content_type:
    case SearchFilterContentTypes.All_videos_content_type:
    case SearchFilterContentTypes.All_images_content_type:
      return '';
    case SearchFilterContentTypes.Sound_effects:
      return 'sound-effects';
    case 'motionbackgrounds':
      return SearchFilterContentTypes.Motion_bgs.toString();
    default:
      return contentType.toString();
  }
}

/**
 * Get media type for flat search urls. Currently only used for video searches
 * e.g. /{mediaType}/search/{keyword}
 * @param contentType
 */
function getMediaTypePath(contentType: SearchFilterContentTypes): string {
  switch (contentType) {
    case SearchFilterContentTypes.Footage:
      return 'video';
    case SearchFilterContentTypes.Motion_bgs:
      return 'motion-graphics';
    case SearchFilterContentTypes.Templates:
      return 'templates';
    case SearchFilterContentTypes.Premiere_pro_templates:
      return 'templates/premiere-pro-templates';
    case SearchFilterContentTypes.After_effects:
      return 'templates/after-effects-templates';
    case SearchFilterContentTypes.Davinci_resolve:
      return 'templates/davinci-resolve-templates';
    case SearchFilterContentTypes.Apple_motion:
      return 'templates/apple-motion-templates';
    default:
      return 'all-video';
  }
}

function getSearchRouteByContentType(
  contentType: SearchFilterContentTypes
): string {
  switch (contentType) {
    case SearchFilterContentTypes.All_videos_content_type:
    case SearchFilterContentTypes.Footage:
    case SearchFilterContentTypes.Motion_bgs:
    case SearchFilterContentTypes.Templates:
    case SearchFilterContentTypes.Premiere_pro_templates:
    case SearchFilterContentTypes.After_effects:
    case SearchFilterContentTypes.Davinci_resolve:
    case SearchFilterContentTypes.Apple_motion:
      return VIDEO_SEARCH_ROUTE;
    case SearchFilterContentTypes.All_audio_content_type:
    case SearchFilterContentTypes.Music:
    case SearchFilterContentTypes.Sound_effects:
      return AUDIO_SEARCH_ROUTE;
    case SearchFilterContentTypes.All_images_content_type:
    case SearchFilterContentTypes.Illustrations:
    case SearchFilterContentTypes.Photos:
    case SearchFilterContentTypes.Vectors:
    case SearchFilterContentTypes.Snapshots:
      return IMAGE_SEARCH_ROUTE;
    default:
      return null;
  }
}

function isAssetId(searchTerm) {
  return /^SB[VIA]-\d{9,}$/i.test(searchTerm);
}
function getSearchRouteByAssetId(assetId: string): string {
  const contentInitial = assetId.substring(2, 3);
  switch (contentInitial) {
    case 'V':
      return VIDEO_SEARCH_ROUTE;
    case 'I':
      return IMAGE_SEARCH_ROUTE;
    case 'A':
      return AUDIO_SEARCH_ROUTE;
    default:
      return null;
  }
}

export const getTrendingTagLinks = (
  keyword: string,
  contentType: SearchFilterContentTypes
) => {
  const trendingTagLinksMap = {
    [SearchFilterContentTypes.All_videos_content_type]: `/video/search/${keyword}`,
    [SearchFilterContentTypes.Templates]: `/video/search/${keyword}?media-type=templates`,
    [SearchFilterContentTypes.Premiere_pro_templates]: `/templates/premiere-pro-templates/search/${keyword}`,
    [SearchFilterContentTypes.After_effects]: `/templates/after-effects-templates/search/${keyword}`,
    [SearchFilterContentTypes.Davinci_resolve]: `/templates/davinci-resolve-templates/search/${keyword}`,
    [SearchFilterContentTypes.Apple_motion]: `/templates/apple-motion-templates/search/${keyword}`,
    [SearchFilterContentTypes.All_audio_content_type]: `/audio/search/${keyword}`,
    [SearchFilterContentTypes.All_images_content_type]: `/images/search/${keyword}`,
    [SearchFilterContentTypes.Footage]: `/video/search/${keyword}`,
    [SearchFilterContentTypes.Motion_bgs]: `/motion-graphics/search/${keyword}`,
    [SearchFilterContentTypes.Music]: `/audio/search/${keyword}?media-type=music`,
    [SearchFilterContentTypes.Sound_effects]: `/audio/search/${keyword}?media-type=sound-effects`,
    [SearchFilterContentTypes.Photos]: `/images/search/${keyword}?media-type=photos`,
    [SearchFilterContentTypes.Vectors]: `/images/search/${keyword}?media-type=vectors`,
    [SearchFilterContentTypes.Illustrations]: `/images/search/${keyword}?media-type=illustrations`,
  };
  return trendingTagLinksMap[contentType];
};

export function getTypeaheadPath(
  contentType: SearchFilterContentTypes
): string {
  switch (contentType) {
    case SearchFilterContentTypes.All_audio_content_type:
    case SearchFilterContentTypes.Music:
    case SearchFilterContentTypes.Sound_effects:
      return '/api/audio/typeahead';
    case SearchFilterContentTypes.All_videos_content_type:
    case SearchFilterContentTypes.Footage:
    case SearchFilterContentTypes.Motion_bgs:
    case SearchFilterContentTypes.Templates:
    case SearchFilterContentTypes.Premiere_pro_templates:
    case SearchFilterContentTypes.After_effects:
    case SearchFilterContentTypes.Davinci_resolve:
    case SearchFilterContentTypes.Apple_motion:
      return '/api/video/typeahead';
    case SearchFilterContentTypes.All_images_content_type:
    case SearchFilterContentTypes.Illustrations:
    case SearchFilterContentTypes.Photos:
    case SearchFilterContentTypes.Vectors:
    default:
      return '/api/images/typeahead';
  }
}

export const fetchTypeahead = function (
  contentType: SearchFilterContentTypes,
  query = '',
  typeaheadEnhancementExperiment = false,
  timerTelemetry: () => Promise<void>
) {
  const typeAheadTimer = timerTelemetry;

  return new Promise((resolve, reject) => {
    fetch(
      `${getTypeaheadPath(contentType)}?keywords=${encodeURIComponent(query)}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (typeaheadEnhancementExperiment || data?.data?.keywords?.length) {
          resolve(data.data);
        }
      })
      .catch(reject)
      .finally(typeAheadTimer);
  });
};
